import type { Nurse, UserContactProfile } from "@dutyon/apis";
import { addCompanyAdmin, findAllNurses, mapClientError, queryKeys } from "@dutyon/apis";
import { Button, Group, modals, notifications, Stack, Text, TextInput } from "@dutyon/ui-kit";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { getCompanyAdminErrorFeedback } from "../../utils/company-admin-error";

interface CompanyAdminSearchModalProps {
  companyId: string;
  onSuccess?: () => void;
}

export function CompanyAdminSearchModal({ companyId, onSuccess }: CompanyAdminSearchModalProps) {
  const [searchEmail, setSearchEmail] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [searchError, setSearchError] = useState<string>();
  const queryClient = useQueryClient();

  const {
    data: searchResult,
    refetch: searchUser,
    isFetching: isSearching,
    isError: isSearchFailed,
    error: searchFailure,
  } = useQuery({
    queryKey: queryKeys.companies.adminSearch(companyId, submittedEmail),
    queryFn: () =>
      findAllNurses({
        page: 1,
        limit: 10,
        companyId,
        email: submittedEmail,
      }),
    enabled: submittedEmail.length > 0,
    retry: false,
  });

  const addAdminMutation = useMutation({
    mutationFn: (userId: string) => addCompanyAdmin({ companyId, userId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.companies.admins(companyId) });
      notifications.show({
        title: "관리자 추가",
        message: "관리자가 성공적으로 추가되었습니다.",
        color: "green",
      });
      onSuccess?.();
      modals.closeAll();
    },
    onError: (error) => {
      const feedback = getCompanyAdminErrorFeedback(
        mapClientError(error, { fallback: "관리자 추가에 실패했습니다." }),
        "ADD",
      );
      if (feedback.shouldRefresh) {
        queryClient.invalidateQueries({ queryKey: queryKeys.companies.admins(companyId) });
      }
      notifications.show({
        title: feedback.title,
        message: feedback.message,
        color: "red",
      });
    },
  });

  const handleSearch = () => {
    const normalizedEmail = searchEmail.trim();
    if (!normalizedEmail) {
      setSearchError("검색할 이메일을 입력해 주세요.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setSearchError("올바른 이메일 주소를 입력해 주세요.");
      return;
    }

    setSearchError(undefined);
    if (normalizedEmail === submittedEmail) {
      searchUser();
      return;
    }
    setSubmittedEmail(normalizedEmail);
  };

  const formatDate = (dateString: Date | string) => {
    return new Date(dateString).toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <>
      <Group align="flex-end" mb="md">
        <TextInput
          label="사용자 이메일 검색"
          placeholder="example@email.com"
          value={searchEmail}
          error={searchError}
          onChange={(e) => {
            setSearchEmail(e.currentTarget.value);
            if (searchError) setSearchError(undefined);
          }}
          style={{ flex: 1 }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSearch();
            }
          }}
        />
        <Button onClick={handleSearch} loading={isSearching}>
          검색
        </Button>
      </Group>

      {isSearchFailed ? (
        <Text c="red" size="sm" ta="center" mt="md" role="alert">
          {
            mapClientError(searchFailure, {
              fallback: "사용자 검색에 실패했습니다. 잠시 후 다시 시도해 주세요.",
            }).message
          }
        </Text>
      ) : searchResult?.list && searchResult.list.length > 0 ? (
        <Stack gap="xs">
          <Text size="sm" c="dimmed">
            검색 결과:
          </Text>
          {searchResult.list.map((nurse: Nurse & { user: UserContactProfile }) => (
            <Group
              key={nurse.id}
              justify="space-between"
              p="xs"
              style={{ border: "1px solid #eee", borderRadius: "4px" }}
            >
              <div>
                <Text size="sm" fw={500}>
                  {nurse.user?.name}
                </Text>
                <Text size="xs" c="dimmed">
                  {nurse.user?.email}
                </Text>
                <Text size="xs" c="dimmed">
                  가입일: {nurse.joinDate ? formatDate(nurse.joinDate) : "-"}
                </Text>
              </div>
              <Button
                size="xs"
                onClick={() => addAdminMutation.mutate(nurse.userId)}
                loading={addAdminMutation.isPending}
              >
                추가
              </Button>
            </Group>
          ))}
        </Stack>
      ) : (
        searchResult && (
          <Text c="dimmed" size="sm" ta="center" mt="md">
            해당 이메일을 가진 사용자를 찾을 수 없습니다.
          </Text>
        )
      )}
    </>
  );
}
